import * as React from 'react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Drawer } from '@/components/primitives/Drawer'
import { Button } from '@/components/primitives/Button'
import { Input } from '@/components/primitives/Input'
import { FormField } from '@/components/primitives/FormField'
import { useToast } from '@/components/feedback/ToastProvider'
import { createResident, updateResident, type Resident } from './api'
import { listFacilities, type Facility } from '@/features/facilities/api'
import { ApiError } from '@/lib/api'
import { formatPhoneInput, combinePhoneCode, isValidFax } from '@/lib/phone'
import '@/pages/ManagePages.css'

const schema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  mrn: z.string().optional().or(z.literal('')),
  dob: z.string().optional().or(z.literal('')).refine(
    (v) => !v || v <= new Date().toISOString().split('T')[0]!,
    { message: 'Date of birth cannot be in the future' },
  ),
  room: z.string().optional().or(z.literal('')),
  sex: z.enum(['male', 'female', 'other', 'unknown']).optional().or(z.literal('')),
  phone: z.string().optional().or(z.literal('')),
  phone_code: z.string().optional().or(z.literal('')),
  pcp_name: z.string().optional().or(z.literal('')),
  pcp_fax: z.string().optional().or(z.literal('')).refine(
    (v) => !v || isValidFax(v),
    { message: 'Enter a valid fax number (e.g. (312) 555-0100)' },
  ),
})

type Form = z.infer<typeof schema>

interface Props {
  open: boolean
  onClose: () => void
  facilityId?: string
  resident?: Resident
  onCreated?: (r: Resident) => void
}

export function AddResidentDrawer({ open, onClose, facilityId, resident, onCreated }: Props): React.JSX.Element {
  const qc = useQueryClient()
  const toast = useToast()
  const isEdit = !!resident
  const needsFacilityPick = !facilityId && !isEdit
  const [pickedFacility, setPickedFacility] = useState<Facility | null>(null)
  const [facilityError, setFacilityError] = useState('')

  const activeFacilityId = facilityId ?? resident?.facility_id ?? pickedFacility?.id

  const { data: facilities = [] } = useQuery({
    queryKey: ['facilities'],
    queryFn: listFacilities,
    enabled: needsFacilityPick && open,
  })

  // Parse existing phone into code + number for edit mode
  function parsePhone(raw: string | null | undefined): { code: string; num: string } {
    if (!raw) return { code: '+1', num: '' }
    const m = raw.match(/^(\+\d+)\s*(.*)$/)
    if (m) return { code: m[1] ?? '+1', num: m[2] ?? '' }
    return { code: '+1', num: raw }
  }

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: resident?.first_name ?? '',
      last_name: resident?.last_name ?? '',
      mrn: resident?.mrn ?? '',
      dob: resident?.dob ?? '',
      room: resident?.room ?? '',
      sex: (resident?.sex as Form['sex']) ?? '',
      phone: parsePhone(resident?.phone).num,
      phone_code: parsePhone(resident?.phone).code,
      pcp_name: resident?.pcp_name ?? '',
      pcp_fax: resident?.pcp_fax ?? '',
    },
  })

  // Reset form whenever resident changes (drawer re-opens for different resident)
  React.useEffect(() => {
    if (open) {
      const { code, num } = parsePhone(resident?.phone)
      reset({
        first_name: resident?.first_name ?? '',
        last_name: resident?.last_name ?? '',
        mrn: resident?.mrn ?? '',
        dob: resident?.dob ?? '',
        room: resident?.room ?? '',
        sex: (resident?.sex as Form['sex']) ?? '',
        phone: num,
        phone_code: code,
        pcp_name: resident?.pcp_name ?? '',
        pcp_fax: resident?.pcp_fax ?? '',
      })
    }
  }, [open, resident, reset])

  const mutation = useMutation({
    mutationFn: (input: Form) => {
      if (isEdit && resident) {
        return updateResident(resident.id, {
          first_name: input.first_name.trim(),
          last_name: input.last_name.trim(),
          mrn: input.mrn?.trim() || null,
          dob: input.dob?.trim() || null,
          room: input.room?.trim() || null,
          sex: (input.sex as 'male' | 'female' | 'other' | 'unknown' | undefined) || null,
          phone: combinePhoneCode(input.phone ?? '', input.phone_code ?? '') || null,
          pcp_name: input.pcp_name?.trim() || null,
          pcp_fax: input.pcp_fax?.trim() || null,
        })
      }
      return createResident({
        facility_id: activeFacilityId!,
        first_name: input.first_name.trim(),
        last_name: input.last_name.trim(),
        mrn: input.mrn?.trim() || null,
        dob: input.dob?.trim() || null,
        room: input.room?.trim() || null,
        sex: (input.sex as 'male' | 'female' | 'other' | 'unknown' | undefined) || null,
        phone: combinePhoneCode(input.phone ?? '', input.phone_code ?? '') || null,
        pcp_name: input.pcp_name?.trim() || null,
        pcp_fax: input.pcp_fax?.trim() || null,
      })
    },
    onSuccess: (r) => {
      toast.success(
        isEdit ? 'Resident updated' : 'Resident added',
        isEdit ? `${r.first_name} ${r.last_name} has been updated.` : `${r.first_name} ${r.last_name} is now in your facility.`,
      )
      reset()
      setPickedFacility(null)
      void qc.invalidateQueries({ queryKey: ['residents'] })
      onCreated?.(r)
      onClose()
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Try again in a moment.'
      toast.error(isEdit ? "Couldn't update resident" : "Couldn't add resident", msg)
    },
  })

  function handleClose() {
    reset()
    setPickedFacility(null)
    setFacilityError('')
    onClose()
  }

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      size="lg"
      title={isEdit ? `Edit resident` : 'Add resident'}
      subtitle={
        isEdit
          ? `Editing ${resident?.first_name} ${resident?.last_name}`
          : 'Add a resident manually. Use CSV import for bulk.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} type="button">
            Cancel
          </Button>
          <Button
            type="submit"
            form="add-resident-form"
            loading={mutation.isPending}
          >
            {isEdit ? 'Save changes' : 'Add resident'}
          </Button>
        </>
      }
    >
      <form
        id="add-resident-form"
        onSubmit={(e) => {
          if (needsFacilityPick && !pickedFacility) {
            e.preventDefault()
            setFacilityError('Please select a facility.')
            return
          }
          void handleSubmit((v) => mutation.mutate(v))(e)
        }}
        className="rf-form"
        noValidate
      >
        {/* Facility (SUPER_ADMIN — pick destination facility) */}
        {needsFacilityPick && (
          <FormField label="Facility" htmlFor="facility-pick" required error={facilityError}>
            <select
              id="facility-pick"
              className="select"
              value={pickedFacility?.id ?? ''}
              onChange={(e) => {
                const f = facilities.find((x) => x.id === e.target.value) ?? null
                setPickedFacility(f)
                setFacilityError('')
              }}
            >
              <option value="" disabled>— select a facility —</option>
              {facilities.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </FormField>
        )}

        {/* ── Resident details ─────────────────────────────────── */}
        <section className="rf-section">
          <p className="rf-section-title">Resident details</p>
          <div className="drawer-2col">
            <FormField label="First name" htmlFor="first_name" required error={errors.first_name?.message}>
              <Input id="first_name" autoComplete="off" placeholder="First name" invalid={!!errors.first_name} {...register('first_name')} />
            </FormField>
            <FormField label="Last name" htmlFor="last_name" required error={errors.last_name?.message}>
              <Input id="last_name" autoComplete="off" placeholder="Last name" invalid={!!errors.last_name} {...register('last_name')} />
            </FormField>
          </div>
          <div className="drawer-2col">
            <FormField label="MRN" htmlFor="mrn">
              <Input id="mrn" autoComplete="off" placeholder="MRN-1006" {...register('mrn')} />
            </FormField>
            <FormField label="Date of birth" htmlFor="dob">
              <Input id="dob" type="date" max={new Date().toISOString().split('T')[0]} {...register('dob')} />
            </FormField>
          </div>
          <FormField label="Sex" htmlFor="sex">
            <select id="sex" className="select" {...register('sex')}>
              <option value="">— select —</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </FormField>
        </section>

        {/* ── Contact ──────────────────────────────────────────── */}
        <section className="rf-section">
          <p className="rf-section-title">Contact</p>
          <div className="drawer-phone-row">
            <FormField label="Code" htmlFor="phone_code">
              <select id="phone_code" className="select" {...register('phone_code')}>
                <option value="+1">+1</option>
                <option value="+91">+91</option>
                <option value="+41">+41</option>
              </select>
            </FormField>
            <FormField label="Phone" htmlFor="phone">
              <Input
                id="phone"
                type="tel"
                autoComplete="off"
                placeholder="(509) 555-4112"
                {...register('phone')}
                onChange={(e) => setValue('phone', formatPhoneInput(e.target.value))}
              />
            </FormField>
          </div>
        </section>

        {/* ── Primary care provider ────────────────────────────── */}
        <section className="rf-section">
          <p className="rf-section-title">Primary care provider</p>
          <p className="rf-section-hint">Vitals are faxed here automatically on schedule.</p>
          <div className="drawer-2col">
            <FormField label="PCP name" htmlFor="pcp_name">
              <Input id="pcp_name" autoComplete="off" placeholder="Dr. Karen Whitfield" {...register('pcp_name')} />
            </FormField>
            <FormField label="PCP fax" htmlFor="pcp_fax" error={errors.pcp_fax?.message}>
              <Input id="pcp_fax" type="tel" autoComplete="off" placeholder="(312) 555-0100" {...register('pcp_fax')} />
            </FormField>
          </div>
        </section>
      </form>
    </Drawer>
  )
}
